import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import api from '@/lib/api';
import {
  ChartBar,
  TrendUp,
  Buildings,
  ArrowClockwise,
  Export,
  CurrencyInr,
  Receipt,
  Warning,
  ArrowSquareOut,
  ShoppingCart,
  CreditCard,
} from '@phosphor-icons/react';

const MONTHS = [
  { value: '1', label: 'January' }, { value: '2', label: 'February' },
  { value: '3', label: 'March' }, { value: '4', label: 'April' },
  { value: '5', label: 'May' }, { value: '6', label: 'June' },
  { value: '7', label: 'July' }, { value: '8', label: 'August' },
  { value: '9', label: 'September' }, { value: '10', label: 'October' },
  { value: '11', label: 'November' }, { value: '12', label: 'December' },
];

export default function RevenueTrackerPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(String(new Date().getMonth() + 1));
  const [year, setYear] = useState(String(new Date().getFullYear()));

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/dashboard/revenue-tracker', { params: { month, year } });
      setData(response.data);
    } catch (error) {
      console.error('Failed to load revenue tracker:', error);
    } finally {
      setLoading(false);
    }
  }, [month, year]);

  useEffect(() => { loadData(); }, [loadData]);

  const drillClinic = (clinic) => {
    const m = month.padStart(2, '0');
    const start = `${year}-${m}-01`;
    const nextM = parseInt(month) === 12 ? 1 : parseInt(month) + 1;
    const nextY = parseInt(month) === 12 ? parseInt(year) + 1 : parseInt(year);
    const end = `${nextY}-${String(nextM).padStart(2, '0')}-01`;
    const q = new URLSearchParams({
      clinic_id: clinic.clinic_id,
      clinic_name: clinic.name,
      start_date: start,
      end_date: end,
      title: `${clinic.name} — ${MONTHS[parseInt(month)-1]?.label} ${year}`,
    }).toString();
    navigate(`/invoices?${q}`);
  };

  const drillState = (state, stateData) => {
    const m = month.padStart(2, '0');
    const start = `${year}-${m}-01`;
    const q = new URLSearchParams({
      start_date: start,
      end_date: '',
      title: `${state} — All Clinics (${stateData.clinics.length})`,
    }).toString();
    navigate(`/invoices?${q}`);
  };

  const drillGrandTotal = () => {
    const m = month.padStart(2, '0');
    const start = `${year}-${m}-01`;
    const q = new URLSearchParams({
      start_date: start,
      end_date: '',
      title: `All Clinics — ${MONTHS[parseInt(month)-1]?.label} ${year}`,
    }).toString();
    navigate(`/invoices?${q}`);
  };

  const gt = data?.grand_totals || {};
  const hasSoCn = (gt.sales_orders > 0 || gt.credit_notes > 0);

  if (loading) {
    return (
      <div data-testid="revenue-tracker-page">
        <PageHeader title="Daily Revenue Tracker" subtitle="Loading..." />
        <div className="page-content">
          <div className="space-y-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-white border border-[#E2E8F0] rounded-lg p-5">
                <div className="skeleton h-6 w-48 mb-3 rounded" />
                <div className="skeleton h-4 w-full rounded" />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="revenue-tracker-page">
      <PageHeader
        title="Daily Revenue Tracker"
        subtitle={`Clinic-wise MTD Revenue - ${MONTHS[parseInt(month)-1]?.label} ${year}`}
        actions={
          <div className="flex gap-2">
            <Button variant="outline" onClick={loadData}>
              <ArrowClockwise size={16} className="mr-2" />
              Refresh
            </Button>
            <Button className="bg-[#0F4C81] hover:bg-[#0B3C68]" data-testid="export-tracker-btn">
              <Export size={16} className="mr-2" />
              Export
            </Button>
          </div>
        }
      />

      <div className="page-content">
        {/* Period Selector */}
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 mb-6" data-testid="tracker-date-filters">
          <div className="flex flex-wrap items-center gap-3">
            <Select value={month} onValueChange={setMonth}>
              <SelectTrigger className="w-36" data-testid="tracker-month-select">
                <SelectValue placeholder="Month" />
              </SelectTrigger>
              <SelectContent>
                {MONTHS.map(m => (
                  <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={year} onValueChange={setYear}>
              <SelectTrigger className="w-24" data-testid="tracker-year-select">
                <SelectValue placeholder="Year" />
              </SelectTrigger>
              <SelectContent>
                {['2024', '2025', '2026'].map(y => (
                  <SelectItem key={y} value={y}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Grand Total Cards */}
        {data?.grand_totals && (
          <div className={`grid grid-cols-2 ${hasSoCn ? 'md:grid-cols-3 lg:grid-cols-6' : 'md:grid-cols-4'} gap-4 mb-6`}>
            <div className="stats-card cursor-pointer group hover:border-[#0F4C81] transition-colors" onClick={drillGrandTotal} data-testid="drill-grand-revenue">
              <div className="flex items-center gap-2">
                <CurrencyInr size={18} className="text-[#0F4C81]" />
                <p className="stats-card-label">{hasSoCn ? 'MTD Net Revenue' : 'MTD Revenue'}</p>
                <ArrowSquareOut size={12} className="text-[#64748B] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="stats-card-value text-[#0F4C81]" data-testid="grand-total-revenue">
                {formatCurrency(hasSoCn ? gt.net_revenue : gt.revenue)}
              </p>
              {hasSoCn && <p className="text-[9px] text-[#64748B] mt-0.5">Inv + SO - CN</p>}
            </div>
            {hasSoCn && (
              <>
                <div className="stats-card cursor-pointer hover:border-emerald-400 transition-colors" onClick={() => navigate('/sales-credits?tab=sales-orders')} data-testid="grand-so">
                  <div className="flex items-center gap-2">
                    <ShoppingCart size={18} className="text-emerald-600" />
                    <p className="stats-card-label">Sales Orders (Confirmed)</p>
                  </div>
                  <p className="stats-card-value text-emerald-700">{formatCurrency(gt.sales_orders)}</p>
                  <p className="text-[9px] text-[#64748B] mt-0.5">{gt.sales_orders_count} orders</p>
                </div>
                <div className="stats-card cursor-pointer hover:border-red-300 transition-colors" onClick={() => navigate('/sales-credits?tab=credit-notes')} data-testid="grand-cn">
                  <div className="flex items-center gap-2">
                    <CreditCard size={18} className="text-red-500" />
                    <p className="stats-card-label">Credit Notes</p>
                  </div>
                  <p className="stats-card-value text-red-600">{formatCurrency(gt.credit_notes)}</p>
                  <p className="text-[9px] text-[#64748B] mt-0.5">{gt.credit_notes_count} notes</p>
                </div>
              </>
            )}
            <div className="stats-card cursor-pointer group hover:border-[#0F4C81] transition-colors" onClick={drillGrandTotal}>
              <div className="flex items-center gap-2">
                <Receipt size={18} className="text-[#0F4C81]" />
                <p className="stats-card-label">Total Invoices</p>
                <ArrowSquareOut size={12} className="text-[#64748B] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="stats-card-value">{gt.invoice_count}</p>
            </div>
            <div className="stats-card cursor-pointer group hover:border-[#0F4C81] transition-colors" onClick={() => navigate('/invoices?status=overdue&title=Outstanding%20Invoices')}>
              <div className="flex items-center gap-2">
                <Warning size={18} className="text-[#DC2626]" />
                <p className="stats-card-label">Outstanding</p>
                <ArrowSquareOut size={12} className="text-[#64748B] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
              <p className="stats-card-value text-[#DC2626]">{formatCurrency(gt.outstanding)}</p>
            </div>
            <div className="stats-card">
              <div className="flex items-center gap-2">
                <Buildings size={18} className="text-[#0F4C81]" />
                <p className="stats-card-label">Active Clinics</p>
              </div>
              <p className="stats-card-value">{gt.clinic_count}</p>
            </div>
          </div>
        )}

        {/* State-wise Clinic Tables */}
        {data?.state_groups && Object.entries(data.state_groups).map(([state, stateData]) => (
          <div key={state} className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden mb-4" data-testid={`state-group-${state.toLowerCase().replace(/\s+/g, '-')}`}>
            {/* State Header — CLICKABLE */}
            <div className="bg-[#0F4C81] text-white px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-[#0B3C68] transition-colors" onClick={() => drillState(state, stateData)} data-testid={`drill-state-${state.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="flex items-center gap-2">
                <Buildings size={18} />
                <h3 className="font-medium font-['Plus_Jakarta_Sans']">{state}</h3>
                <span className="text-xs bg-white/20 px-2 py-0.5 rounded-full">{stateData.clinics.length} clinics</span>
              </div>
              <div className="flex items-center gap-4 text-sm">
                <span>Revenue: <b>{formatCurrency(stateData.totals.revenue)}</b></span>
                <span>Invoices: <b>{stateData.totals.invoices}</b></span>
                <ArrowSquareOut size={14} className="opacity-60" />
              </div>
            </div>
            
            {/* Clinic Table */}
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#F8FAFC] border-b border-[#E2E8F0]">
                  <th className="text-left p-3 font-medium text-[#64748B]">Clinic</th>
                  <th className="text-right p-3 font-medium text-[#64748B]">Revenue</th>
                  <th className="text-right p-3 font-medium text-[#64748B]">Outstanding</th>
                  <th className="text-center p-3 font-medium text-[#64748B]">Invoices</th>
                  <th className="text-center p-3 font-medium text-[#64748B]">Paid</th>
                  <th className="text-center p-3 font-medium text-[#64748B]">Overdue</th>
                  <th className="text-right p-3 font-medium text-[#64748B]">Avg Ticket</th>
                </tr>
              </thead>
              <tbody>
                {stateData.clinics.map((clinic) => {
                  const revPct = stateData.totals.revenue > 0 ? (clinic.revenue / stateData.totals.revenue * 100) : 0;
                  return (
                    <tr key={clinic.clinic_id} className="border-b border-[#E2E8F0] hover:bg-[#F8FAFC] transition-colors cursor-pointer group" onClick={() => drillClinic(clinic)} data-testid={`drill-clinic-${clinic.clinic_id}`}>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-[#0F172A]">{clinic.name}</div>
                          <ArrowSquareOut size={12} className="text-[#64748B] opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                        </div>
                        <div className="w-full bg-[#E2E8F0] rounded-full h-1 mt-1">
                          <div className="bg-[#0F4C81] h-1 rounded-full transition-all" style={{ width: `${Math.min(revPct, 100)}%` }} />
                        </div>
                      </td>
                      <td className="p-3 text-right font-semibold text-[#0F4C81]">{formatCurrency(clinic.revenue)}</td>
                      <td className="p-3 text-right">
                        <span className={clinic.outstanding > 0 ? 'text-[#DC2626] font-medium' : 'text-[#64748B]'}>
                          {formatCurrency(clinic.outstanding)}
                        </span>
                      </td>
                      <td className="p-3 text-center">{clinic.invoice_count}</td>
                      <td className="p-3 text-center text-green-600 font-medium">{clinic.paid}</td>
                      <td className="p-3 text-center">
                        {clinic.overdue > 0 ? (
                          <span className="text-[#DC2626] font-medium">{clinic.overdue}</span>
                        ) : (
                          <span className="text-[#64748B]">0</span>
                        )}
                      </td>
                      <td className="p-3 text-right text-[#64748B]">{formatCurrency(clinic.avg_ticket)}</td>
                    </tr>
                  );
                })}
                {/* State Total Row */}
                <tr className="bg-[#F8FAFC] font-semibold">
                  <td className="p-3">Total {state}</td>
                  <td className="p-3 text-right text-[#0F4C81]">{formatCurrency(stateData.totals.revenue)}</td>
                  <td className="p-3 text-right text-[#DC2626]">{formatCurrency(stateData.totals.balance)}</td>
                  <td className="p-3 text-center">{stateData.totals.invoices}</td>
                  <td className="p-3 text-center" colSpan={3}></td>
                </tr>
              </tbody>
            </table>
          </div>
        ))}

        {/* Daily Breakdown */}
        {data?.daily_breakdown && data.daily_breakdown.length > 0 && (
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden">
            <div className="p-4 border-b border-[#E2E8F0]">
              <h3 className="font-medium font-['Plus_Jakarta_Sans'] flex items-center gap-2">
                <ChartBar size={18} className="text-[#0F4C81]" />
                Daily Revenue Trend {hasSoCn && '(Net Revenue = Invoices + SO - CN)'}
              </h3>
            </div>
            <div className="p-4 overflow-x-auto">
              <div className="flex items-end gap-1 min-h-[120px]">
                {data.daily_breakdown.map((day) => {
                  const maxRev = Math.max(...data.daily_breakdown.map(d => hasSoCn ? (d.net_revenue || d.revenue) : d.revenue));
                  const val = hasSoCn ? (day.net_revenue || day.revenue) : day.revenue;
                  const pct = maxRev > 0 ? (val / maxRev * 100) : 0;
                  const dayNum = day.date.split('-')[2];
                  return (
                    <div key={day.date} className="flex flex-col items-center gap-1 flex-1 min-w-[24px]" title={`${day.date}: ${formatCurrency(val)} (${day.count} inv${day.sales_orders ? ', SO: ' + formatCurrency(day.sales_orders) : ''}${day.credit_notes ? ', CN: ' + formatCurrency(day.credit_notes) : ''})`}>
                      <span className="text-[8px] text-[#64748B] -rotate-45 whitespace-nowrap">{formatCurrency(val)}</span>
                      <div className="w-full bg-[#0F4C81] rounded-t" style={{ height: `${Math.max(pct, 2)}px`, minHeight: '2px' }} />
                      <span className="text-[10px] text-[#64748B]">{dayNum}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
